import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { TopSticky } from 'inside-out/components/shared/topSticky';
import { GreyBar } from 'inside-out/components/shared/greyBar';
import { Header } from 'inside-out/components/shared/header';
import { Footer } from 'inside-out/components/shared/footer';
import { LinkList } from 'inside-out/components/shared/linkList';
import { Link } from 'inside-out/components/shared/link';
import { SingleFieldForm } from 'inside-out/components/shared/singleFieldForm';
import CloseIcon from 'inside-out/images/close-icon.svg';

import { NavManager } from 'inside-out/managers/navManager';
import { AuthManager } from 'inside-out/managers/authManager';
import { encode } from 'inside-out/managers/formManager';

import {
	skipLinksStyle,
	wrapperStyle,
	mobileNavStyle,
	mobileNavOverlay,
	primaryMobileNavStyle,
	secondaryMobileNavStyle,
	closeIconStyle,
} from './main.styles';

export default class MainLayout extends React.PureComponent {
	static propTypes = {
		children: PropTypes.func.isRequired,
		data: PropTypes.shape({
			allContentJson: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							baseTitle: PropTypes.string,
							top_sticky: PropTypes.shape({
								enabled: PropTypes.bool.isRequired,
								color: PropTypes.string,
								text_color: PropTypes.string,
								content: PropTypes.string,
							}),
							header_links: PropTypes.arrayOf(
								PropTypes.shape({
									link_text: PropTypes.string.isRequired,
									link_url: PropTypes.string.isRequired,
								})
							).isRequired,
							footer_links: PropTypes.arrayOf(
								PropTypes.shape({
									section_name: PropTypes.string.isRequired,
									links: PropTypes.arrayOf(
										PropTypes.shape({
											link_text: PropTypes.string.isRequired,
											link_url: PropTypes.string.isRequired,
										})
									).isRequired,
								})
							).isRequired,
							mobile_links: PropTypes.arrayOf(
								PropTypes.shape({
									links: PropTypes.arrayOf(
										PropTypes.shape({
											link_text: PropTypes.string.isRequired,
											link_url: PropTypes.string.isRequired,
										})
									).isRequired,
								})
							).isRequired,
						}),
					})
				),
			}),
		}),
	};

	state = {
		navOpen: false,
		isLoggedIn: AuthManager.sharedInstance.isLoggedIn,
		subscribeEmail: '',
		isTopStickyVisible: false,
	};

	navOpenDidChange = navOpen => {
		if (navOpen) {
			document.documentElement.classList.add('noScroll');
		} else {
			document.documentElement.classList.remove('noScroll');
		}
		this.setState({ navOpen });
	};

	loggedInDidChange = () => {
		this.setState({ isLoggedIn: AuthManager.sharedInstance.isLoggedIn });
	};

	componentWillMount() {
		NavManager.subscribe(this.navOpenDidChange);
		AuthManager.sharedInstance.subscribe(this.loggedInDidChange);

		if (typeof window !== 'undefined' && sessionStorage.getItem('top_sticky_closed') !== '1') {
			this.setState({ isTopStickyVisible: true });
		}
	}

	componentWillUnmount() {
		NavManager.unsubscribe(this.navOpenDidChange);
		AuthManager.sharedInstance.unsubscribe(this.loggedInDidChange);
	}

	closeTopSticky = () => {
		if (typeof window === 'undefined') return;
		sessionStorage.setItem('top_sticky_closed', '1');
		this.setState({ isTopStickyVisible: false });
	};

	render() {
		const { children } = this.props;
		const pageSettings = this.props.data.page.edges[0].node;
		const { top_sticky } = pageSettings;
		const hasTopSticky = top_sticky && top_sticky.enabled && this.state.isTopStickyVisible;
		const topMargin = hasTopSticky ? 56 : 0;

		return (
			<React.Fragment>
				<div className={wrapperStyle}>
					<Helmet titleTemplate={`${pageSettings.baseTitle} - %s`} />
					<a href="#content" className={skipLinksStyle}>
						Skip links
					</a>
					{hasTopSticky && (
						<TopSticky
							bgColor={top_sticky.color}
							color={top_sticky.text_color}
							content={top_sticky.content}
							onClose={this.closeTopSticky}
						/>
					)}
					<GreyBar topMargin={topMargin} />

					<Header
						topMargin={topMargin}
						navLinks={pageSettings.header_links.map(link => ({
							to: link.link_url,
							text: link.link_text,
							children: (link.submenu || []).map(sublink => ({
								to: sublink.link_url,
								text: sublink.link_text,
							})),
						}))}
					/>
					<main id="content">{children()}</main>

					<Footer
						sections={[
							...pageSettings.footer_links.map(section => ({
								title: section.section_name,
								component: (
									<LinkList
										links={section.links.map(link => ({
											to: link.link_url,
											text: link.link_text,
										}))}
									/>
								),
							})),
							{
								title: 'Newsletter',
								colspan: 2,
								component: (
									<SingleFieldForm
										key="subscribe-form"
										inputId="subscribe-form"
										inputName="email"
										name="subscribe"
										netlify
										placeholderText="email@example.com"
										label="Subscribe to our newsletter!"
										buttonContent="Subscribe"
										inputWidth={181}
										onSubmit={async (e, value) => {
											e.preventDefault();
											try {
												const result = await fetch('/', {
													method: 'POST',
													body: encode({
														'form-name': 'subscribe',
														botField: '',
														email: value,
													}),
												});

												alert('Successfully signed up!');
											} catch (error) {
												console.error(error);
											}
										}}
									/>
								),
							},
						]}
					/>
				</div>
				{this.state.navOpen && <div className={mobileNavOverlay} id="mobile-nav-overlay" />}
				{this.state.navOpen && (
					<div className={mobileNavStyle} id="mobile-nav">
						<button className={closeIconStyle} onClick={NavManager.hideNavigation}>
							<img src={CloseIcon} alt="Close" />
						</button>
						{pageSettings.mobile_links.map((section, index) => (
							<nav
								key={index}
								className={index % 2 ? secondaryMobileNavStyle : primaryMobileNavStyle}
							>
								{section.links.map((link, index) => (
									<Link key={index} to={link.link_url} onClick={NavManager.hideNavigation}>
										{link.link_text}
									</Link>
								))}
							</nav>
						))}

						<nav>
							{this.state.isLoggedIn && (
								<React.Fragment>
									<Link to="/my-bookmarks" onClick={NavManager.hideNavigation}>
										Bookmarks
									</Link>
									<Link to="/my-account" onClick={NavManager.hideNavigation}>
										Account Settings
									</Link>
									<Link
										onClick={() => {
											NavManager.hideNavigation();
											AuthManager.sharedInstance.logout();
										}}
									>
										Log Out
									</Link>
								</React.Fragment>
							)}
							{!this.state.isLoggedIn && (
								<React.Fragment>
									<Link to="/log-in" onClick={NavManager.hideNavigation}>
										Log in
									</Link>
									<Link to="/register" onClick={NavManager.hideNavigation}>
										Register
									</Link>
								</React.Fragment>
							)}
						</nav>
					</div>
				)}
			</React.Fragment>
		);
	}
}

export const query = graphql`
	query MainLayoutQuery {
		page: allContentJson(filter: { id: { regex: "\\/site-settings\\.json/" } }) {
			edges {
				node {
					baseTitle
					top_sticky {
						enabled
						color
						text_color
						content
					}
					header_links {
						link_text
						link_url
						submenu {
							link_text
							link_url
						}
					}
					footer_links {
						section_name
						links {
							link_text
							link_url
						}
					}
					mobile_links {
						links {
							link_text
							link_url
						}
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/layouts/main.js